import React, {
  FC,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { Resizer as ResizerOrigin } from "../Resizer";

type UseResizeOptions<K extends PropertyKey> = {
  key: K;
  onColumnWidthChange: (key: K, width: number) => void;
  isLastColumn?: boolean;
  minWidth?: number;
};

export const useResize = <
  E extends HTMLElement,
  K extends PropertyKey = PropertyKey
>({
  key,
  onColumnWidthChange,
  isLastColumn = false,
  minWidth = 260,
}: UseResizeOptions<K>) => {
  const ref = useRef<E>(null);

  const handleMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();

      const startPositionX = event.clientX;
      const startWidth = ref.current?.offsetWidth ?? 0;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const currentPositionX = moveEvent.clientX;
        const positionDiff = currentPositionX - startPositionX;
        const newWidth = startWidth + positionDiff;

        if (ref.current) {
          ref.current.style.width = `${newWidth}px`;
          ref.current.style.minWidth = `${newWidth}px`;
        }
      };

      const handleMouseUp = () => {
        const finalWidth = ref.current?.offsetWidth;

        if (finalWidth) {
          onColumnWidthChange(key, finalWidth);
        }

        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [onColumnWidthChange, key]
  );

  const Resizer = useMemo(() => {
    const component: FC = () => (
      <ResizerOrigin handleMouseDown={handleMouseDown} />
    );

    return component;
  }, [handleMouseDown]);

  useLayoutEffect(() => {
    if (ref.current && minWidth) {
      ref.current.style.width = `${isLastColumn ? "100%" : `${minWidth}px`} `;
      ref.current.style.minWidth = `${minWidth}px`;
    }
  }, [minWidth, isLastColumn]);

  return {
    ref,
    Resizer,
  };
};
